/**
 * entityResolutionConstants.js — shared constants + StyleSheet for the
 * EntityResolutionTab family (main tab + extracted presentational parts).
 * Extracted verbatim from components/EntityResolutionTab.jsx (god-file decomposition).
 */

import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { getAdminAuthHeaders } from '../firebaseConfig';
import _allSportsbooksJson from '../../utils/sportsbooks.json';

export const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL
  || Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL
  || Constants?.manifest?.extra?.FUNCTIONS_BASE_URL
);

export const ENTITY_TYPES = ['player', 'team', 'market', 'league', 'sportsbook'];

const _OBVIOUS_TOKENS = new Set([
  'best', 'top', 'good', 'hot', 'sharp', 'picks', 'pick', 'bets', 'bet', 'odds', 'lines', 'line',
  'tonight', 'today', 'tomorrow', 'the', 'a', 'for', 'me', 'show', 'all', 'my',
  'spreads', 'spread', 'props', 'prop', 'moneylines', 'moneyline', 'ml', 'totals', 'total',
  'puck', 'run', 'points', 'player',
  'nfl', 'nba', 'wnba', 'nhl', 'mlb', 'ncaaf', 'ncaab', 'ufc', 'soccer', 'epl', 'premier', 'league',
  'football', 'basketball', 'baseball', 'hockey', 'mma', 'tennis',
  'draftkings', 'fanduel', 'betmgm', 'caesars', 'fanatics', 'betrivers', 'espnbet', 'hardrock',
]);

/** League/book/market queries the classifier already understands. Not a review item. */
export function isSelfExplanatoryQuery(input) {
  const text = String(input || '').toLowerCase();
  if (!/\b(nfl|nba|wnba|nhl|mlb|ncaaf|ncaab|ufc|soccer|epl|premier league|football|basketball|baseball|hockey)\b/.test(text)) {
    return false;
  }
  const words = text.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
  return words.length > 0 && words.every((word) => _OBVIOUS_TOKENS.has(word));
}

export const LEAGUE_BASELINE = {
  nba: 'basketball', wnba: 'basketball', ncaab: 'basketball', ncaaw: 'basketball',
  nfl: 'football', ncaaf: 'football', cfl: 'football',
  mlb: 'baseball', nhl: 'hockey',
  ufc: 'mma', pfl: 'mma', bellator: 'mma', boxing: 'boxing',
  atp: 'tennis', wta: 'tennis',
  pga: 'golf', lpga: 'golf', liv: 'golf',
  'england_-_premier_league': 'soccer', 'spain_-_la_liga': 'soccer', 'germany_-_bundesliga': 'soccer',
  'italy_-_serie_a': 'soccer', 'france_-_ligue_1': 'soccer', 'uefa_-_champions_league': 'soccer',
  'usa_-_major_league_soccer': 'soccer', 'mexico_-_liga_mx': 'soccer',
  formula_1: 'motorsport', nascar_cup: 'motorsport',
  nrl: 'rugby_league', ipl: 'cricket',
  cs2: 'esports', league_of_legends: 'esports', valorant: 'esports',
  afl: 'australian_football',
};
export const STATUS_OPTIONS = ['pending', 'auto', 'confirmed', 'corrected', 'rejected', 'user_reported'];
// Direction options are now dynamic per market (see directionOptions memo in CorrectionForm)
export const STATUS_COLORS = { pending: '#f59e0b', auto: '#f59e0b', confirmed: '#10b981', corrected: '#3b82f6', rejected: '#ef4444', user_reported: '#a78bfa' };
export const TYPE_ICONS = { player: 'person', team: 'people', market: 'trending-up', league: 'globe', sportsbook: 'book' };
export const QUERY_TYPE_INFO = {
  SPECIFIC_PLAYER_MARKET: { label: 'Player + Line', icon: 'person', color: '#c084fc', desc: 'Player with a specific line (e.g. "LeBron points over 25")' },
  PLAYER_PROP: { label: 'Player Prop', icon: 'person', color: '#a78bfa', desc: 'General player prop query (e.g. "KAT rebounds")' },
  TEAM_MARKETS: { label: 'Team Market', icon: 'people', color: '#60a5fa', desc: 'All markets for a team (e.g. "Lakers spread")' },
  SPECIFIC_TEAM_MARKET: { label: 'Team + Market', icon: 'people', color: '#38bdf8', desc: 'A specific market for one team (e.g. "Celtics moneyline")' },
  MATCHUP_QUERY: { label: 'Matchup', icon: 'swap-horizontal', color: '#f59e0b', desc: 'Two teams head-to-head (e.g. "Lakers vs Celtics")' },
  LEAGUE_WIDE_QUERY: { label: 'League', icon: 'globe', color: '#6b7280', desc: 'All games in a league (e.g. "NBA tonight")' },
  BEST_BETS_ALL: { label: 'Best Bets', icon: 'trophy', color: '#10b981', desc: 'Best value bets across sports' },
  GENERIC: { label: 'General', icon: 'search', color: '#6b7280', desc: 'Broad or unclassified query' },
};
export const CONFIDENCE_LEVELS = [
  { min: 0.9, label: 'High', color: '#10b981', icon: 'shield-checkmark', range: '90–100%', desc: 'Confirmed by an admin or corrected' },
  { min: 0.7, label: 'Medium', color: '#f59e0b', icon: 'shield-half', range: '70–89%', desc: 'Auto-learned from successful queries' },
  { min: 0, label: 'Low', color: '#ef4444', icon: 'alert-circle', range: 'Below 70%', desc: 'Unverified — needs review' },
];
export const QUERY_TYPES = ['SGP', 'LIVE', 'STRAIGHT'];
export const ALL_SPORTSBOOK_NAMES = _allSportsbooksJson.map(b => b.name);

export const st = {
  wrap: { flex: 1 },
  inner: { padding: 10, paddingBottom: 60 },
  title: { color: '#fff', fontSize: 17, fontWeight: '700', marginBottom: 10 },
  errTxt: { color: '#ef4444', marginBottom: 8, fontSize: 13 },
  emptyTxt: { color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 28, fontSize: 13 },

  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  statCard: { minWidth: 120, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.02)', alignItems: 'center', flexGrow: 1 },
  statVal: { fontSize: 24, fontWeight: '800' },
  statLbl: { color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 2 },

  tabs: { flexDirection: 'row', gap: 5, marginBottom: 14 },
  tab: { paddingVertical: 7, paddingHorizontal: 14, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.04)' },
  tabOn: { backgroundColor: 'rgba(255,255,255,0.14)' },
  tabTxt: { color: 'rgba(255,255,255,0.4)', fontWeight: '600', fontSize: 13 },
  tabTxtOn: { color: '#fff' },

  filterRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 8, flexWrap: 'wrap' },
  filterLbl: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: '600', marginRight: 2 },
  search: { height: 36, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', color: '#fff', marginBottom: 8, fontSize: 13 },

  chip: { paddingVertical: 4, paddingHorizontal: 9, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  chipOn: { backgroundColor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
  chipTxt: { color: 'rgba(255,255,255,0.5)', fontWeight: '600', fontSize: 11 },
  chipTxtOn: { color: '#fff' },
  chipRow: { flexDirection: 'row', gap: 4, flexWrap: 'wrap' },
  chipSm: { paddingVertical: 3, paddingHorizontal: 7, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  chipSmTxt: { color: 'rgba(255,255,255,0.45)', fontWeight: '600', fontSize: 10 },

  card: { padding: 14, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)', backgroundColor: 'rgba(255,255,255,0.02)', marginBottom: 8 },
  cardSel: { borderColor: 'rgba(139,92,246,0.4)', backgroundColor: 'rgba(139,92,246,0.04)' },
  cardHighlight: { borderColor: 'rgba(167,139,250,0.5)', backgroundColor: 'rgba(167,139,250,0.06)', borderWidth: 2 },
  cardTop: { flexDirection: 'row', alignItems: 'flex-start' },

  cbWrap: { paddingRight: 10, paddingTop: 2 },
  cb: { width: 18, height: 18, borderRadius: 4, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  cbOn: { backgroundColor: '#fff', borderColor: '#fff' },

  badges: { flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 6, flexWrap: 'wrap' },
  badge: { paddingVertical: 1, paddingHorizontal: 7, borderRadius: 8 },
  badgeTxt: { color: '#fff', fontSize: 9, fontWeight: '700' },
  badgeOut: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 1, paddingHorizontal: 7, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' },
  badgeOutTxt: { color: 'rgba(255,255,255,0.6)', fontSize: 9, fontWeight: '600' },
  meta: { color: 'rgba(255,255,255,0.25)', fontSize: 9 },

  inputBox: { padding: 8, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)' },
  boxLabel: { color: 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: '700', letterSpacing: 0.8, marginBottom: 2 },
  inputTxt: { color: '#f59e0b', fontSize: 13, fontWeight: '600' },

  mapGrid: { padding: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)' },
  mapCols: { flexDirection: 'row', flexWrap: 'wrap', gap: 2 },
  mapCell: { flexDirection: 'row', alignItems: 'center', gap: 6, width: '48%', paddingVertical: 4, paddingHorizontal: 4 },
  mapCellDot: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  mapKey: { color: 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: '700', letterSpacing: 0.5 },
  mapVal: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: '600' },
  mapMissing: { color: 'rgba(255,255,255,0.1)', fontSize: 11, fontStyle: 'italic' },
  commentBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 5, marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.04)' },
  commentTxt: { color: 'rgba(167,139,250,0.7)', fontSize: 11, fontStyle: 'italic', flex: 1 },

  actions: { flexDirection: 'row', gap: 6, marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.04)' },
  actBtn: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 5, paddingHorizontal: 9, borderRadius: 7, backgroundColor: 'rgba(255,255,255,0.04)' },
  actTxt: { fontWeight: '600', fontSize: 11 },

  editPanel: { marginTop: 10, padding: 14, borderRadius: 8, backgroundColor: 'rgba(59,130,246,0.03)', borderWidth: 1, borderColor: 'rgba(59,130,246,0.12)' },
  editTitle: { color: '#fff', fontSize: 14, fontWeight: '700', marginBottom: 1 },
  editSub: { color: 'rgba(255,255,255,0.35)', fontSize: 11, marginBottom: 12 },

  corrForm: {},
  field: { marginBottom: 10 },
  fLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: '600', marginBottom: 3 },
  fInput: { height: 36, paddingHorizontal: 11, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13 },
  clearBtn: { position: 'absolute', right: 8 },

  ddBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 },
  dropdown: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 8, backgroundColor: 'rgba(18,18,18,0.98)', marginTop: 3, ...(Platform.OS === 'web' ? { position: 'relative', zIndex: 100 } : {}) },
  ddItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 7, paddingHorizontal: 11, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  ddName: { color: '#fff', fontSize: 12, fontWeight: '500', flex: 1 },
  ddMeta: { color: 'rgba(255,255,255,0.3)', fontSize: 10, marginLeft: 8 },

  marketTags: { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginBottom: 6 },
  marketTag: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 3, paddingHorizontal: 8, borderRadius: 6, backgroundColor: 'rgba(245,158,11,0.08)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.2)' },
  marketTagTxt: { color: '#f59e0b', fontSize: 11, fontWeight: '600' },

  summaryBox: { padding: 10, borderRadius: 8, backgroundColor: 'rgba(16,185,129,0.04)', borderWidth: 1, borderColor: 'rgba(16,185,129,0.12)', marginBottom: 10 },
  summaryTitle: { color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: '700', letterSpacing: 0.8, marginBottom: 4 },
  summaryInput: { color: '#f59e0b', fontSize: 12, fontWeight: '600', marginBottom: 6 },
  summaryFields: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  summaryTag: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 3, paddingHorizontal: 8, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)' },
  summaryKey: { color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: '600' },
  summaryVal: { color: '#fff', fontSize: 11, fontWeight: '700' },

  formActions: { flexDirection: 'row', gap: 8, marginTop: 6 },
  saveBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 8, paddingHorizontal: 16, borderRadius: 8, backgroundColor: '#3b82f6' },
  saveTxt: { color: '#fff', fontWeight: '700', fontSize: 12 },
  cancelBtn: { paddingVertical: 8, paddingHorizontal: 16, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.06)' },
  cancelTxt: { color: 'rgba(255,255,255,0.5)', fontWeight: '600', fontSize: 12 },

  bulkBar: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 8, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.04)', marginBottom: 8 },
  bulkTxt: { color: '#fff', fontWeight: '700', fontSize: 12, marginRight: 'auto' },
  bulkGreen: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 5, paddingHorizontal: 10, borderRadius: 7, backgroundColor: '#10b981' },
  bulkRed: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingVertical: 5, paddingHorizontal: 10, borderRadius: 7, backgroundColor: '#ef4444' },
  bulkBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 11 },

  selAll: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, marginBottom: 4 },
  selAllTxt: { color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: '600' },

  metricsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  metricCard: { minWidth: 200, flexGrow: 1, flexBasis: '30%', padding: 14, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)', backgroundColor: 'rgba(255,255,255,0.02)' },
  metricTitle: { color: '#fff', fontWeight: '700', fontSize: 13, marginBottom: 8 },
  mRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  mKey: { color: 'rgba(255,255,255,0.65)', fontSize: 12, flex: 1, marginLeft: 3 },
  mVal: { color: '#fff', fontWeight: '700', fontSize: 12, minWidth: 36, textAlign: 'right' },
  dot: { width: 7, height: 7, borderRadius: 4, marginRight: 3 },
  topRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  topRank: { color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: '600', width: 22 },
  topIn: { color: '#f59e0b', fontSize: 11, flex: 1 },
  topOut: { color: '#10b981', fontSize: 11, flex: 1 },
  topCt: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: '700', width: 30, textAlign: 'right' },

  legendWrap: { marginBottom: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)', backgroundColor: 'rgba(255,255,255,0.015)', overflow: 'hidden' },
  legendToggle: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 12 },
  legendToggleTxt: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: '600', flex: 1 },
  legendBody: { paddingHorizontal: 12, paddingBottom: 12 },
  legendSection: {},
  legendSectionTitle: { color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: '700', letterSpacing: 0.8, marginBottom: 6 },
  legendRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 3 },
  legendDot: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  legendLabel: { fontSize: 11, fontWeight: '700' },
  legendDesc: { color: 'rgba(255,255,255,0.3)', fontSize: 10 },

  aiSugBox: { marginTop: 8, padding: 10, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(167,139,250,0.15)', backgroundColor: 'rgba(167,139,250,0.04)' },
  aiSugTitle: { color: '#a78bfa', fontSize: 11, fontWeight: '700' },
  aiSugConf: { color: 'rgba(167,139,250,0.5)', fontSize: 10, fontWeight: '600' },
  aiAcceptBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 10, borderRadius: 6, backgroundColor: 'rgba(167,139,250,0.12)', borderWidth: 1, borderColor: 'rgba(167,139,250,0.25)' },
  aiAcceptTxt: { color: '#a78bfa', fontSize: 11, fontWeight: '700' },
  aiReasoning: { color: 'rgba(167,139,250,0.45)', fontSize: 10, fontStyle: 'italic', marginTop: 6 },
  aiAnalyzing: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, backgroundColor: 'rgba(167,139,250,0.06)', marginBottom: 10 },
  aiAnalyzingTxt: { color: 'rgba(167,139,250,0.5)', fontSize: 11, fontWeight: '600' },
  aiRerunBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(167,139,250,0.2)', marginBottom: 10, alignSelf: 'flex-start' },
  aiRerunTxt: { color: '#a78bfa', fontSize: 11, fontWeight: '600' },
};

// Admin calls to the entityResolutions function carry the signed-in admin's ID
// token: every action except the no-results form's (getDropdownData, userFeedback)
// requires it (Sep 30, 2026 — the endpoint used to accept anyone).
export async function erFetch(url, init = {}) {
    let authHeaders = {};
    try { authHeaders = await getAdminAuthHeaders(); } catch (_) { /* signed out → the call gets 401 */ }
    return fetch(url, { ...init, headers: { ...(init.headers || {}), ...authHeaders } });
}
